import { generateRestoSlug } from './RestoSlugUtils';
import { isBrowser } from './isServer';

export interface SEOData {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: any;
}

export class SEOManager {
  private static instance: SEOManager;
  private baseUrl = 'https://kauwenofspauwen.be';

  private constructor() {}

  static getInstance(): SEOManager {
    if (!SEOManager.instance) {
      SEOManager.instance = new SEOManager();
    }
    return SEOManager.instance;
  }

  updateMetaTags(data: SEOData, language: string = 'en'): void {
    if (!isBrowser) return;

    // Update title
    document.title = data.title;
    this.updateMetaTag('name', 'title', data.title);
    this.updateMetaTag('property', 'og:title', data.title);
    this.updateMetaTag('name', 'twitter:title', data.title);

    // Update description
    this.updateMetaTag('name', 'description', data.description);
    this.updateMetaTag('property', 'og:description', data.description);
    this.updateMetaTag('name', 'twitter:description', data.description);

    // Update keywords
    this.updateMetaTag('name', 'keywords', data.keywords.join(', '));

    // Update language
    document.documentElement.lang = language;
    this.updateMetaTag('name', 'language', language === 'nl' ? 'Dutch' : 'English');

    // Update canonical URL
    if (data.canonicalUrl) {
      this.updateLinkTag('canonical', data.canonicalUrl);
    }

    // Update Open Graph URL
    this.updateMetaTag('property', 'og:url', data.canonicalUrl || window.location.href);
    this.updateMetaTag('name', 'twitter:url', data.canonicalUrl || window.location.href);

    // Update locale
    const locale = language === 'nl' ? 'nl_BE' : 'en_US';
    this.updateMetaTag('property', 'og:locale', locale);

    // Add structured data
    if (data.structuredData) {
      this.addStructuredData(data.structuredData);
    }
  }

  private updateMetaTag(attr: string, key: string, value: string): void {
    let element = document.querySelector(`meta[${attr}="${key}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attr, key);
      document.head.appendChild(element);
    }
    element.setAttribute('content', value);
  }

  private updateLinkTag(rel: string, href: string): void {
    let element = document.querySelector(`link[rel="${rel}"]`);
    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', rel);
      document.head.appendChild(element);
    }
    element.setAttribute('href', href);
  }

  private addStructuredData(data: any): void {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }

  getSEODataForPage(page: string, language: string): SEOData {
    const isNL = language === 'nl';

    switch (page) {
      case 'home':
        return {
          title: isNL
            ? 'Kauwen of Spauwen - Controleer Restaurant Hygiëne België | Voedselveiligheid Kaart'
            : 'Kauwen of Spauwen - Check Restaurant Hygiene Belgium | Food Safety Map',
          description: isNL
            ? 'Controleer restaurant hygiëne scores voor je gaat eten. Bekijk inspectie resultaten, lees meldingen van andere gasten. Meld voedselvergiftiging anoniem.'
            : 'Check restaurant hygiene scores before dining out. View inspection results, read reports from other diners. Report food poisoning anonymously.',
          keywords: isNL
            ? ['voedselvergiftiging melden', 'restaurant hygiëne belgië', 'voedselveiligheid', 'favv inspectie', 'restaurant controle', 'ziek van eten', 'slechte hygiëne restaurant', 'food poisoning belgië']
            : ['report food poisoning', 'restaurant hygiene belgium', 'food safety', 'food inspection', 'restaurant ratings', 'sick from food', 'poor hygiene restaurant', 'food poisoning belgium'],
          structuredData: this.getHomePageStructuredData(language)
        };

      case 'report':
        return {
          title: isNL
            ? 'Voedselvergiftiging Melden - Restaurant Klacht Indienen België'
            : 'Report Food Poisoning - Submit Restaurant Complaint Belgium',
          description: isNL
            ? 'Meld direct voedselvergiftiging of slechte hygiëne in Belgische restaurants. Anoniem een klacht indienen over voedselveiligheid. Waarschuw andere bezoekers.'
            : 'Report food poisoning or poor hygiene in Belgian restaurants immediately. Submit anonymous food safety complaints. Warn other visitors.',
          keywords: isNL
            ? ['voedselvergiftiging melden', 'restaurant klacht', 'favv melding', 'ziek van restaurant', 'slechte hygiëne melden', 'voedselveiligheid klacht']
            : ['report food poisoning', 'restaurant complaint', 'food safety report', 'sick from restaurant', 'report poor hygiene', 'food safety complaint']
        };

      case 'faq':
        return {
          title: isNL
            ? 'Veelgestelde Vragen over Voedselvergiftiging & Restaurant Hygiëne | Kauwen of Spauwen'
            : 'Food Poisoning & Restaurant Hygiene FAQs | Kauwen of Spauwen',
          description: isNL
            ? 'Antwoorden op veelgestelde vragen over voedselvergiftiging melden, symptomen, hygiëne scores en uw rechten in België.'
            : 'Answers to common questions about reporting food poisoning, symptoms, hygiene scores, and your rights in Belgium.',
          keywords: isNL
            ? ['voedselvergiftiging FAQ', 'veelgestelde vragen', 'voedselvergiftiging symptomen', 'restaurant hygiëne vragen', 'voedselveiligheid FAQ']
            : ['food poisoning FAQ', 'frequently asked questions', 'food poisoning symptoms', 'restaurant hygiene questions', 'food safety FAQ'],
          structuredData: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: isNL ? [
              { '@type': 'Question', name: 'Hoe meld ik voedselvergiftiging?', acceptedAnswer: { '@type': 'Answer', text: 'Je kunt voedselvergiftiging anoniem melden op ons platform door het restaurant te zoeken en een melding in te dienen. Dit helpt andere gasten direct te waarschuwen.' } },
              { '@type': 'Question', name: 'Wat zijn symptomen van voedselvergiftiging?', acceptedAnswer: { '@type': 'Answer', text: 'Veelvoorkomende symptomen zijn misselijkheid, braken, diarree, buikkrampen, koorts en hoofdpijn. Symptomen beginnen meestal 2-6 uur na het eten van besmet voedsel.' } },
              { '@type': 'Question', name: 'Hoe werkt dit platform?', acceptedAnswer: { '@type': 'Answer', text: 'Ons platform biedt snelle, anonieme meldingen van voedselvergiftiging. Meldingen zijn direct zichtbaar om anderen te helpen niet ziek te worden.' } },
              { '@type': 'Question', name: 'Hoe lang duurt voedselvergiftiging?', acceptedAnswer: { '@type': 'Answer', text: 'De meeste gevallen van voedselvergiftiging duren 1-3 dagen. Zoek medische hulp als symptomen langer aanhouden, u hoge koorts heeft, bloed in de ontlasting, of tekenen van uitdroging.' } },
              { '@type': 'Question', name: 'Kan ik zien welke restaurants slechte hygiëne hebben?', acceptedAnswer: { '@type': 'Answer', text: 'Ja, wij tonen hygiëne inspectie scores en voedselvergiftiging meldingen. Zoek naar restaurants met slechte beoordelingen of recente meldingen.' } },
              { '@type': 'Question', name: 'Wat veroorzaakt voedselvergiftiging in restaurants?', acceptedAnswer: { '@type': 'Answer', text: 'Veelvoorkomende oorzaken zijn slechte hygiëne, onjuiste voedselopslag, kruisbesmetting, onvoldoende gaar voedsel en zieke voedselbereiders.' } },
              { '@type': 'Question', name: 'Wat moet ik doen bij ernstige gevallen?', acceptedAnswer: { '@type': 'Answer', text: 'Bij ernstige gevallen, zoek onmiddellijk medische hulp. Bel 112 in België of ga naar de spoedafdeling van het dichtstbijzijnde ziekenhuis.' } },
            ] : [
              { '@type': 'Question', name: 'How do I report food poisoning?', acceptedAnswer: { '@type': 'Answer', text: 'You can report food poisoning anonymously on our platform by finding the restaurant and submitting a report. This helps warn other diners immediately.' } },
              { '@type': 'Question', name: 'What are symptoms of food poisoning?', acceptedAnswer: { '@type': 'Answer', text: 'Common food poisoning symptoms include nausea, vomiting, diarrhea, stomach cramps, fever, and headache. Symptoms usually start 2-6 hours after eating contaminated food.' } },
              { '@type': 'Question', name: 'How does this platform work?', acceptedAnswer: { '@type': 'Answer', text: 'Our platform offers fast, anonymous food poisoning reporting. Reports are visible immediately to help others avoid getting sick.' } },
              { '@type': 'Question', name: 'How long does food poisoning last?', acceptedAnswer: { '@type': 'Answer', text: 'Most food poisoning cases last 1-3 days. Seek medical attention if symptoms persist longer, you have high fever, blood in stool, or signs of dehydration.' } },
              { '@type': 'Question', name: 'Can I see which restaurants have poor hygiene?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, we display hygiene inspection scores and food poisoning reports. Look for restaurants with poor ratings or recent food poisoning reports.' } },
              { '@type': 'Question', name: 'What causes food poisoning in restaurants?', acceptedAnswer: { '@type': 'Answer', text: 'Common causes include poor hygiene, improper food storage, cross-contamination, undercooked food, and sick food handlers. Our reports help identify these issues.' } },
              { '@type': 'Question', name: 'What should I do in serious cases?', acceptedAnswer: { '@type': 'Answer', text: 'For serious cases, seek immediate medical attention. Contact emergency services (112 in Belgium) or visit your nearest hospital emergency room.' } },
            ]
          }
        };

      case 'how-to':
        return {
          title: isNL
            ? 'Hoe Voedselvergiftiging Melden - Stappenplan België'
            : 'How to Report Food Poisoning - Step by Step Guide Belgium',
          description: isNL
            ? 'Leer hoe je voedselvergiftiging meldt in België. Stappenplan voor het melden bij FAVV en lokale autoriteiten. Wat te doen bij voedselvergiftiging.'
            : 'Learn how to report food poisoning in Belgium. Step-by-step guide for reporting to FASFC and local authorities. What to do when food poisoned.',
          keywords: isNL
            ? ['hoe voedselvergiftiging melden', 'favv contact', 'wat doen voedselvergiftiging', 'stappenplan melding', 'belgische voedselinspectie']
            : ['how to report food poisoning', 'fasfc contact', 'what to do food poisoning', 'reporting guide', 'belgian food inspection']
        };

      case 'wall-of-shame':
        return {
          title: isNL
            ? 'Slechtste Restaurants België - Wall of Shame | Slechte Hygiëne'
            : 'Worst Restaurants Belgium - Wall of Shame | Poor Hygiene',
          description: isNL
            ? 'Lijst van restaurants met slechte hygiëne scores in België. Vermijd deze restaurants met voedselveiligheid problemen. Gebaseerd op officiële inspecties.'
            : 'List of restaurants with poor hygiene scores in Belgium. Avoid these restaurants with food safety issues. Based on official inspections.',
          keywords: isNL
            ? ['slechtste restaurants', 'slechte hygiëne', 'te vermijden restaurants', 'wall of shame', 'gevaarlijke restaurants belgië']
            : ['worst restaurants', 'poor hygiene', 'restaurants to avoid', 'wall of shame', 'dangerous restaurants belgium']
        };

      case 'ultimate-guide':
        return {
          title: isNL
            ? 'Ultieme Gids: Voedselvergiftiging Melden België | FAVV Melding'
            : 'Ultimate Guide: Report Food Poisoning Belgium | FASFC Reporting',
          description: isNL
            ? 'Complete gids voor het melden van voedselvergiftiging in België. Stap-voor-stap instructies voor FAVV melding, uw rechten, wat te verwachten. Officiële procedures uitgelegd.'
            : 'Complete guide to reporting food poisoning in Belgium. Step-by-step instructions for FASFC reporting, your rights, what to expect. Official procedures explained.',
          keywords: isNL
            ? ['voedselvergiftiging melden gids', 'FAVV melding procedure', 'hoe meld ik voedselvergiftiging', 'consumentenrechten voedselvergiftiging', 'officiële melding voedselvergiftiging']
            : ['food poisoning reporting guide', 'FASFC reporting procedure', 'how to report food poisoning', 'consumer rights food poisoning', 'official food poisoning report'],
          structuredData: {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: isNL ? 'Voedselvergiftiging Melden in België' : 'How to Report Food Poisoning in Belgium',
            description: isNL 
              ? 'Stap-voor-stap gids voor het melden van voedselvergiftiging aan FAVV/AFSCA'
              : 'Step-by-step guide to report food poisoning to FASFC/AFSCA',
            step: [
              {
                '@type': 'HowToStep',
                name: isNL ? 'Verzamel Informatie' : 'Gather Information',
                text: isNL ? 'Verzamel restaurantgegevens, datum, symptomen' : 'Collect restaurant details, date, symptoms'
              },
              {
                '@type': 'HowToStep',
                name: isNL ? 'Meld bij FAVV' : 'Report to FASFC',
                text: isNL ? 'Contact FAVV via telefoon, email of online' : 'Contact FASFC via phone, email or online'
              },
              {
                '@type': 'HowToStep',
                name: isNL ? 'Volg Op' : 'Follow Up',
                text: isNL ? 'Wacht op bevestiging en onderzoek' : 'Wait for confirmation and investigation'
              }
            ]
          }
        };

      case 'consumer-rights':
        return {
          title: isNL
            ? 'Consumentenrechten bij Voedselvergiftiging België | Schadevergoeding'
            : 'Consumer Rights Food Poisoning Belgium | Compensation Claims',
          description: isNL
            ? 'Uw wettelijke rechten bij voedselvergiftiging in België. Leer over schadevergoeding, medische kosten vergoeding, juridische stappen. Consumentenbescherming uitgelegd.'
            : 'Your legal rights when suffering food poisoning in Belgium. Learn about compensation claims, medical costs reimbursement, legal steps. Consumer protection explained.',
          keywords: isNL
            ? ['consumentenrechten voedselvergiftiging', 'schadevergoeding voedselvergiftiging', 'juridische rechten restaurant', 'medische kosten vergoeding', 'belgisch consumentenrecht']
            : ['consumer rights food poisoning', 'compensation food poisoning', 'legal rights restaurant', 'medical costs reimbursement', 'belgian consumer law'],
          structuredData: {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: isNL ? 'Consumentenrechten bij Voedselvergiftiging' : 'Consumer Rights for Food Poisoning',
            description: isNL 
              ? 'Uw rechten en compensatiemogelijkheden bij voedselvergiftiging in België'
              : 'Your rights and compensation options for food poisoning in Belgium',
            articleSection: 'Legal Rights',
            keywords: isNL 
              ? 'consumentenrechten, voedselvergiftiging, schadevergoeding, België'
              : 'consumer rights, food poisoning, compensation, Belgium'
          }
        };

      case 'managing-symptoms':
        return {
          title: isNL
            ? 'Voedselvergiftiging Symptomen Behandelen | Wat te Doen België'
            : 'Managing Food Poisoning Symptoms | What to Do Belgium',
          description: isNL
            ? 'Leer hoe voedselvergiftiging symptomen te behandelen. Wanneer medische hulp zoeken, uitdroging voorkomen, natuurlijke remedies. Medisch advies voor België.'
            : 'Learn how to manage food poisoning symptoms. When to seek medical help, prevent dehydration, natural remedies. Medical guidance for Belgium.',
          keywords: isNL
            ? ['voedselvergiftiging symptomen', 'wat doen voedselvergiftiging', 'uitdroging voorkomen', 'wanneer naar dokter', 'natuurlijke remedies voedselvergiftiging']
            : ['food poisoning symptoms', 'what to do food poisoning', 'prevent dehydration', 'when to see doctor', 'natural remedies food poisoning'],
          structuredData: {
            '@context': 'https://schema.org',
            '@type': 'MedicalWebPage',
            name: isNL ? 'Voedselvergiftiging Symptomen Beheren' : 'Managing Food Poisoning Symptoms',
            description: isNL 
              ? 'Gids voor het beheren van voedselvergiftiging symptomen en wanneer medische hulp te zoeken'
              : 'Guide to managing food poisoning symptoms and when to seek medical help',
            medicalAudience: {
              '@type': 'MedicalAudience',
              audienceType: 'Patient'
            },
            disclaimer: isNL 
              ? 'Deze informatie is alleen voor educatieve doeleinden. Raadpleeg een arts voor medisch advies.'
              : 'This information is for educational purposes only. Consult a doctor for medical advice.'
          }
        };

      case 'city':
        return {
          title: isNL
            ? 'Restaurant Hygiëne per Stad - Voedselveiligheid België | Kauwen of Spauwen'
            : 'Restaurant Hygiene by City - Food Safety Belgium | Kauwen of Spauwen',
          description: isNL
            ? 'Bekijk restaurant hygiëne scores per stad in België. Vergelijk voedselveiligheid en inspectie resultaten.'
            : 'View restaurant hygiene scores by city in Belgium. Compare food safety and inspection results.',
          keywords: isNL
            ? ['restaurant hygiëne stad', 'voedselveiligheid gemeente', 'inspectie resultaten', 'restaurant scores']
            : ['restaurant hygiene city', 'food safety municipality', 'inspection results', 'restaurant scores'],
        };

      case 'province':
        return {
          title: isNL
            ? 'Restaurant Hygiëne per Provincie - België | Kauwen of Spauwen'
            : 'Restaurant Hygiene by Province - Belgium | Kauwen of Spauwen',
          description: isNL
            ? 'Bekijk restaurant hygiëne scores per provincie in België.'
            : 'View restaurant hygiene scores by province in Belgium.',
          keywords: isNL
            ? ['restaurant hygiëne provincie', 'voedselveiligheid', 'inspectie resultaten']
            : ['restaurant hygiene province', 'food safety', 'inspection results'],
        };

      case 'recent-reports':
        return {
          title: isNL
            ? 'Recente Voedselvergiftiging Meldingen België | Kauwen of Spauwen'
            : 'Recent Food Safety Reports Belgium | Kauwen of Spauwen',
          description: isNL
            ? 'Bekijk de nieuwste meldingen van voedselvergiftiging en slechte hygiëne in Belgische restaurants. Lees ervaringen van andere consumenten.'
            : 'View the latest food poisoning and poor hygiene reports for Belgian restaurants. Read experiences from other consumers.',
          keywords: isNL
            ? ['voedselvergiftiging meldingen', 'recente meldingen', 'restaurant klachten', 'voedselveiligheid ervaringen', 'hygiëne meldingen']
            : ['food poisoning reports', 'recent reports', 'restaurant complaints', 'food safety experiences', 'hygiene reports'],
          structuredData: {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: isNL ? 'Recente Voedselvergiftiging Meldingen' : 'Recent Food Safety Reports',
            description: isNL
              ? 'Overzicht van recente voedselvergiftiging meldingen in België'
              : 'Overview of recent food poisoning reports in Belgium',
            url: `${this.baseUrl}/${language}/recent-reports`
          }
        };

      case 'trending':
        return {
          title: isNL
            ? 'Trending Restaurant Hygiëne Veranderingen België | Kauwen of Spauwen'
            : 'Trending Restaurant Hygiene Changes Belgium | Kauwen of Spauwen',
          description: isNL
            ? 'Ontdek welke restaurants in België hun hygiëne score hebben verbeterd of verslechterd. Bekijk de laatste inspectie trends.'
            : 'Discover which restaurants in Belgium have improved or declined in hygiene scores. View the latest inspection trends.',
          keywords: isNL
            ? ['restaurant hygiëne trends', 'inspectie veranderingen', 'verbeterde restaurants', 'verslechterde restaurants', 'hygiëne scores']
            : ['restaurant hygiene trends', 'inspection changes', 'improving restaurants', 'declining restaurants', 'hygiene scores'],
          structuredData: {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: isNL ? 'Trending Restaurant Hygiëne Veranderingen' : 'Trending Restaurant Hygiene Changes',
            description: isNL
              ? 'Restaurants met recente hygiëne score veranderingen in België'
              : 'Restaurants with recent hygiene score changes in Belgium',
            url: `${this.baseUrl}/${language}/trending`
          }
        };

      default:
        return this.getSEODataForPage('home', language);
    }
  }

  private getHomePageStructuredData(language: string): any {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Kauwen of Spauwen',
      description: language === 'nl' 
        ? 'Platform voor het melden van voedselvergiftiging en bekijken van restaurant hygiëne scores in België'
        : 'Platform for reporting food poisoning and viewing restaurant hygiene scores in Belgium',
      url: this.baseUrl,
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Any',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.5',
        ratingCount: '1250'
      },
      potentialAction: {
        '@type': 'SearchAction',
        target: `${this.baseUrl}/?search={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    };
  }

  getRestaurantStructuredData(restaurant: any): any {
    return {
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      name: restaurant.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: restaurant.address,
        postalCode: restaurant.postcode,
        addressLocality: restaurant.deelgemeente,
        addressCountry: 'BE'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: restaurant.latitude,
        longitude: restaurant.longitude
      },
      url: `${this.baseUrl}/?restoId=${generateRestoSlug(restaurant.name, restaurant.id)}`,
      aggregateRating: restaurant.forkRating ? {
        '@type': 'AggregateRating',
        ratingValue: restaurant.forkRating,
        bestRating: '5',
        worstRating: '0',
        ratingCount: restaurant.reportCount || 1
      } : undefined
    };
  }
}